import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  RefreshCw,
  X,
  History,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Receipt,
  ShoppingCart,
  ExternalLink,
} from 'lucide-react';
import productService from '../services/productService';
import useFilterPersistence from '../hooks/useFilterPersistence';
import Layout from '../components/Layout';
import ClientSelector from '../components/ClientSelector';

const PriceHistoryPage = () => {
  const navigate = useNavigate();
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState(null);

  // Use filter persistence hook
  const [persistedState, setPersistedState] = useFilterPersistence('price-history', {
    filters: {
      tipDiameterMode: 'range',
      tipDiameter: '',
      tipDiameterMin: '',
      tipDiameterMax: '',
      totalLengthMode: 'range',
      totalLength: '',
      totalLengthMin: '',
      totalLengthMax: '',
      category: '',
      clientId: null,
      clientName: '',
      productName: '',
      productId: ''
    },
    pagination: {
      page: 1,
      pageSize: 20,
      totalCount: 0,
      totalPages: 0
    },
    sorting: {
      sortBy: 'date',
      sortOrder: 'desc'
    }
  });

  // Extract state from persisted state
  const filters = persistedState.filters;
  const pagination = persistedState.pagination;
  const sorting = persistedState.sorting;

  // Update functions
  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  const setPagination = useCallback((newPagination) => {
    setPersistedState(prev => ({
      ...prev,
      pagination: typeof newPagination === 'function' ? newPagination(prev.pagination) : newPagination
    }));
  }, [setPersistedState]);

  const setSorting = (newSorting) => {
    setPersistedState(prev => ({
      ...prev,
      sorting: typeof newSorting === 'function' ? newSorting(prev.sorting) : newSorting
    }));
  };

  // Search function
  const searchPriceHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Build search criteria
      const criteria = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder
      };

      // Tip diameter
      if (filters.tipDiameterMode === 'exact' && filters.tipDiameter) {
        criteria.tipDiameter = parseFloat(filters.tipDiameter);
      } else if (filters.tipDiameterMode === 'range') {
        if (filters.tipDiameterMin) criteria.tipDiameterMin = parseFloat(filters.tipDiameterMin);
        if (filters.tipDiameterMax) criteria.tipDiameterMax = parseFloat(filters.tipDiameterMax);
      }

      // Total length
      if (filters.totalLengthMode === 'exact' && filters.totalLength) {
        criteria.totalLength = parseFloat(filters.totalLength);
      } else if (filters.totalLengthMode === 'range') {
        if (filters.totalLengthMin) criteria.totalLengthMin = parseFloat(filters.totalLengthMin);
        if (filters.totalLengthMax) criteria.totalLengthMax = parseFloat(filters.totalLengthMax);
      }

      // Text filters
      if (filters.category) criteria.category = filters.category;
      if (filters.clientName && filters.clientId !== 'global') {
        criteria.clientName = filters.clientName;
      }
      if (filters.productName) criteria.productName = filters.productName;
      if (filters.productId) criteria.productId = filters.productId;

      const response = await productService.searchAdvancedPriceHistory(criteria);

      setPriceHistory(response.price_history || []);
      setStatistics(response.statistics || null);
      setPagination(prev => ({
        ...prev,
        totalCount: response.pagination?.total_count || 0,
        totalPages: response.pagination?.total_pages || 0
      }));

    } catch (err) {
      console.error('Price history search error:', err);
      setError(err.response?.data?.error || err.message || '搜索价格历史失败');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize, sorting, setPagination]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle search
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    searchPriceHistory();
  };

  // Handle client change
  const handleClientChange = (clientId, clientData) => {
    setFilters(prev => ({
      ...prev,
      clientId: clientId,
      clientName: clientId === 'global' ? '' : (clientData?.name || clientData?.client_name || '')
    }));
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilters({
      tipDiameterMode: 'range',
      tipDiameter: '',
      tipDiameterMin: '',
      tipDiameterMax: '',
      totalLengthMode: 'range',
      totalLength: '',
      totalLengthMin: '',
      totalLengthMax: '',
      category: '',
      clientId: null,
      clientName: '',
      productName: '',
      productId: ''
    });
    setPagination(prev => ({ ...prev, page: 1, totalCount: 0, totalPages: 0 }));
    setPriceHistory([]);
    setStatistics(null);
  };

  // Handle sort
  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Navigate to document detail
  const handleDocumentClick = (item) => {
    if (item.type === 'quotation') {
      navigate(`/quotations/${item.document_object_id}/view`);
    } else if (item.type === 'sales_order') {
      navigate(`/sales/${item.document_object_id}`);
    }
  };

  // Effect to search when pagination or sorting changes (after initial search)
  useEffect(() => {
    if (priceHistory.length > 0 || pagination.totalCount > 0) {
      searchPriceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, sorting.sortBy, sorting.sortOrder]);

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sorting.sortBy !== field) {
      return <ArrowUpDown className="h-4 w-4 text-muted-foreground" />;
    }
    return sorting.sortOrder === 'asc' ?
      <ArrowUp className="h-4 w-4" /> :
      <ArrowDown className="h-4 w-4" />;
  };

  // Format price
  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return `¥${parseFloat(price).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <Layout activeTab="price-history">
      <div className="container mx-auto py-6 px-4 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <History className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">价格历史</h1>
              <p className="text-sm text-muted-foreground">
                按制造参数搜索报价单和销售订单中的历史价格
              </p>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Search Filters */}
        <Card className="mb-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">搜索条件</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Tip Diameter */}
              <div className="space-y-2">
                <Label>刀尖直径 (mm)</Label>
                <div className="flex items-center gap-2 mb-2">
                  <Select
                    value={filters.tipDiameterMode}
                    onValueChange={(value) => handleFilterChange('tipDiameterMode', value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">精确</SelectItem>
                      <SelectItem value="range">范围</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {filters.tipDiameterMode === 'exact' ? (
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="输入精确值"
                    value={filters.tipDiameter}
                    onChange={(e) => handleFilterChange('tipDiameter', e.target.value)}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="最小"
                      value={filters.tipDiameterMin}
                      onChange={(e) => handleFilterChange('tipDiameterMin', e.target.value)}
                    />
                    <span>-</span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="最大"
                      value={filters.tipDiameterMax}
                      onChange={(e) => handleFilterChange('tipDiameterMax', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Total Length */}
              <div className="space-y-2">
                <Label>总长度 (mm)</Label>
                <div className="flex items-center gap-2 mb-2">
                  <Select
                    value={filters.totalLengthMode}
                    onValueChange={(value) => handleFilterChange('totalLengthMode', value)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">精确</SelectItem>
                      <SelectItem value="range">范围</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {filters.totalLengthMode === 'exact' ? (
                  <Input
                    type="number"
                    step="0.1"
                    placeholder="输入精确值"
                    value={filters.totalLength}
                    onChange={(e) => handleFilterChange('totalLength', e.target.value)}
                  />
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="最小"
                      value={filters.totalLengthMin}
                      onChange={(e) => handleFilterChange('totalLengthMin', e.target.value)}
                    />
                    <span>-</span>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="最大"
                      value={filters.totalLengthMax}
                      onChange={(e) => handleFilterChange('totalLengthMax', e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>产品分类</Label>
                <Input
                  placeholder="输入分类关键词"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                />
              </div>

              {/* Client Name */}
              <div className="space-y-2">
                <Label>客户名称</Label>
                <ClientSelector
                  selectedClientId={filters.clientId}
                  selectedClientName={filters.clientName}
                  onClientChange={handleClientChange}
                  showGlobalOption={true}
                  compact={true}
                  placeholder="搜索或选择客户"
                />
              </div>

              {/* Product ID */}
              <div className="space-y-2">
                <Label>产品编号</Label>
                <Input
                  placeholder="输入产品编号"
                  value={filters.productId}
                  onChange={(e) => handleFilterChange('productId', e.target.value)}
                />
              </div>

              {/* Product Name */}
              <div className="space-y-2">
                <Label>产品名称</Label>
                <Input
                  placeholder="输入产品名称"
                  value={filters.productName}
                  onChange={(e) => handleFilterChange('productName', e.target.value)}
                />
              </div>
            </div>

            {/* Search Buttons */}
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={handleSearch} disabled={loading}>
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                搜索
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                <X className="h-4 w-4 mr-2" />
                清空
              </Button>
              <Button
                variant="outline"
                onClick={searchPriceHistory}
                disabled={loading || priceHistory.length === 0}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Summary */}
        {statistics && (
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="text-sm py-1 px-3">
                  <History className="h-3 w-3 mr-1" />
                  共 {statistics.total_records || 0} 条记录
                </Badge>
                <Badge variant="outline" className="text-sm py-1 px-3 text-blue-600 border-blue-300">
                  <Receipt className="h-3 w-3 mr-1" />
                  报价单 {statistics.quotation_count || 0}
                </Badge>
                <Badge variant="outline" className="text-sm py-1 px-3 text-green-600 border-green-300">
                  <ShoppingCart className="h-3 w-3 mr-1" />
                  销售订单 {statistics.sales_order_count || 0}
                </Badge>
                <Badge variant="outline" className="text-sm py-1 px-3">
                  平均价格 {formatPrice(statistics.avg_price)}
                </Badge>
                <Badge variant="outline" className="text-sm py-1 px-3">
                  {statistics.unique_clients || 0} 个客户
                </Badge>
                {statistics.unique_products && (
                  <Badge variant="outline" className="text-sm py-1 px-3">
                    {statistics.unique_products} 个产品
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">加载中...</span>
              </div>
            ) : priceHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                <History className="h-16 w-16 mb-4" />
                <p className="text-lg">暂无价格历史数据</p>
                <p className="text-sm">请输入搜索条件后点击搜索按钮</p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">
                          <button
                            className="flex items-center gap-1 hover:text-primary"
                            onClick={() => handleSort('source_type')}
                          >
                            类型
                            {renderSortIcon('source_type')}
                          </button>
                        </th>
                        <th className="text-left p-3 font-medium">
                          <button
                            className="flex items-center gap-1 hover:text-primary"
                            onClick={() => handleSort('date')}
                          >
                            日期
                            {renderSortIcon('date')}
                          </button>
                        </th>
                        <th className="text-left p-3 font-medium">单据编号</th>
                        <th className="text-left p-3 font-medium">
                          <button
                            className="flex items-center gap-1 hover:text-primary"
                            onClick={() => handleSort('client_name')}
                          >
                            客户
                            {renderSortIcon('client_name')}
                          </button>
                        </th>
                        <th className="text-left p-3 font-medium">
                          <button
                            className="flex items-center gap-1 hover:text-primary"
                            onClick={() => handleSort('product_id')}
                          >
                            产品编号
                            {renderSortIcon('product_id')}
                          </button>
                        </th>
                        <th className="text-left p-3 font-medium">规格</th>
                        <th className="text-right p-3 font-medium">
                          <button
                            className="flex items-center gap-1 hover:text-primary ml-auto"
                            onClick={() => handleSort('quantity')}
                          >
                            数量
                            {renderSortIcon('quantity')}
                          </button>
                        </th>
                        <th className="text-right p-3 font-medium">不含税价</th>
                        <th className="text-right p-3 font-medium">含税价</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceHistory.map((item, index) => (
                        <tr
                          key={item._id || index}
                          className="border-t hover:bg-muted/30 transition-colors"
                        >
                          <td className="p-3">
                            <Badge
                              variant="outline"
                              className={
                                item.type === 'quotation'
                                  ? 'text-blue-600 border-blue-300'
                                  : 'text-green-600 border-green-300'
                              }
                            >
                              {item.type === 'quotation' ? (
                                <Receipt className="h-3 w-3 mr-1" />
                              ) : (
                                <ShoppingCart className="h-3 w-3 mr-1" />
                              )}
                              {item.type === 'quotation' ? '报价单' : '销售订单'}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm">{formatDate(item.date)}</td>
                          <td className="p-3">
                            <button
                              className="flex items-center gap-1 text-primary hover:underline font-medium"
                              onClick={() => handleDocumentClick(item)}
                            >
                              {item.document_id}
                              <ExternalLink className="h-3 w-3" />
                            </button>
                          </td>
                          <td className="p-3 text-sm">{item.client_name || '-'}</td>
                          <td className="p-3 text-sm font-mono">{item.product_id || '-'}</td>
                          <td className="p-3 text-sm">{item.specification || '-'}</td>
                          <td className="p-3 text-right text-sm">{item.quantity || '-'}</td>
                          <td className="p-3 text-right text-sm font-medium">
                            {formatPrice(item.tax_exclusive_price)}
                          </td>
                          <td className="p-3 text-right text-sm font-medium">
                            {formatPrice(item.tax_inclusive_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <div className="text-sm text-muted-foreground">
                      第 {pagination.page} / {pagination.totalPages} 页，共 {pagination.totalCount} 条
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        上一页
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.totalPages}
                      >
                        下一页
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PriceHistoryPage;
